import * as moment from 'moment';

import { Customer, getCustomerCustomFieldValue } from '../customer';
import { propExists } from '../../reporting-core/utils';

export const COLUMN_NAME_ID = 'ID';
export const COLUMN_NAME_FIRST_NAME = 'First name';
export const COLUMN_NAME_LAST_NAME = 'Last name';
export const COLUMN_NAME_PREFERRED_NAME = 'Preferred Name';
export const COLUMN_NAME_BIRTHDAY = 'Birthday';
export const COLUMN_NAME_AGE = 'Age';
export const COLUMN_NAME_EMAIL_ADDRESS = 'Email address';
export const COLUMN_NAME_EMAIL_OPT_IN = 'Email marketing opt-in';
export const COLUMN_NAME_ADDRESS_POSTCODE = 'Address: postcode';
export const COLUMN_NAME_ADDRESS_STREET_1 = 'Address: street line 1';
export const COLUMN_NAME_ADDRESS_STREET_2 = 'Address: street line 2';
export const COLUMN_NAME_ADDRESS_CITY = 'Address: city';
export const COLUMN_NAME_ADDRESS_STATE = 'Address: state';
export const COLUMN_NAME_ADDRESS_COUNTRY = 'Address: country';
export const COLUMN_NAME_TELEPHONE = 'Telephone';
export const COLUMN_NAME_GOVT_ID_NUMBER = 'Govt ID number';
export const COLUMN_NAME_GENDER = 'Gender';
export const COLUMN_NAME_PRONOUNS = 'Pronouns';
export const COLUMN_NAME_BARCODE = 'Barcode';
export const COLUMN_NAME_CHECK_IN_COUNT = 'Lifetime check-in count';
export const COLUMN_NAME_HAS_ACTIVE_MEMBERSHIP = 'Has active membership?';
export const COLUMN_NAME_HAS_ACTIVE_YOUTH_PROGRAMMING_MEMBERSHIP = 'Has active youth programming membership?';
export const COLUMN_NAME_HAS_ACTIVE_NON_YOUTH_PROGRAMMING_MEMBERSHIP = 'Has active non-youth programming membership?';
export const COLUMN_NAME_HAS_MEMBERSHIP_WITH_FUTURE_START = 'Has membership set to start in the future?';
export const COLUMN_NAME_HAS_FROZEN_MEMBERSHIP = 'Has frozen membership?';
export const COLUMN_NAME_NUMBER_OF_AVAILABLE_ENTRY_PASSES = 'Number of available entry passes';
export const COLUMN_NAME_ACTIVE_WAIVER_EXISTS = 'Has active waiver?';
export const COLUMN_NAME_LATEST_WAIVER_EXPIRATION_DATE = 'Waiver expiration date';
export const COLUMN_NAME_ACTIVE_PROFICIENCY_NAMES = 'Proficiencies';
export const COLUMN_NAME_FIRST_CHECK_IN_TIMESTAMP = 'First check-in timestamp';
export const COLUMN_NAME_LATEST_CHECK_IN_TIMESTAMP = 'Latest check-in timestamp';
export const COLUMN_NAME_DAYS_SINCE_LATEST_CHECK_IN = 'Days since last check-in';
export const COLUMN_NAME_FIRST_MEMBERSHIP_TIMESTAMP = 'First membership start timestamp'
export const COLUMN_NAME_ACCOUNT_CREATED_TIMESTAMP = 'Account created timestamp';
export const COLUMN_NAME_HOME_LOCATION_ID = 'Home location ID';
export const COLUMN_NAME_HOME_LOCATION_NAME = 'Home location name';

export const COLUMN_NAME_CHECK_IN_COUNT_IN_RANGE = 'Check-in count in range';

export const COLUMN_NAMES: string[] = [
  COLUMN_NAME_ID,
  COLUMN_NAME_FIRST_NAME,
  COLUMN_NAME_LAST_NAME,
  COLUMN_NAME_PREFERRED_NAME,
  COLUMN_NAME_BIRTHDAY,
  COLUMN_NAME_AGE,
  COLUMN_NAME_EMAIL_ADDRESS,
  COLUMN_NAME_EMAIL_OPT_IN,
  COLUMN_NAME_ADDRESS_POSTCODE,
  COLUMN_NAME_ADDRESS_STREET_1,
  COLUMN_NAME_ADDRESS_STREET_2,
  COLUMN_NAME_ADDRESS_CITY,
  COLUMN_NAME_ADDRESS_STATE,
  COLUMN_NAME_ADDRESS_COUNTRY,
  COLUMN_NAME_TELEPHONE,
  COLUMN_NAME_GOVT_ID_NUMBER,
  COLUMN_NAME_GENDER,
  COLUMN_NAME_PRONOUNS,
  COLUMN_NAME_BARCODE,
  COLUMN_NAME_CHECK_IN_COUNT,
  COLUMN_NAME_HAS_ACTIVE_MEMBERSHIP,
  COLUMN_NAME_HAS_ACTIVE_YOUTH_PROGRAMMING_MEMBERSHIP,
  COLUMN_NAME_HAS_ACTIVE_NON_YOUTH_PROGRAMMING_MEMBERSHIP,
  COLUMN_NAME_HAS_MEMBERSHIP_WITH_FUTURE_START,
  COLUMN_NAME_HAS_FROZEN_MEMBERSHIP,
  COLUMN_NAME_NUMBER_OF_AVAILABLE_ENTRY_PASSES,
  COLUMN_NAME_ACTIVE_WAIVER_EXISTS,
  COLUMN_NAME_LATEST_WAIVER_EXPIRATION_DATE,
  COLUMN_NAME_ACTIVE_PROFICIENCY_NAMES,
  COLUMN_NAME_FIRST_CHECK_IN_TIMESTAMP,
  COLUMN_NAME_LATEST_CHECK_IN_TIMESTAMP,
  COLUMN_NAME_DAYS_SINCE_LATEST_CHECK_IN,
  COLUMN_NAME_FIRST_MEMBERSHIP_TIMESTAMP,
  COLUMN_NAME_ACCOUNT_CREATED_TIMESTAMP,
  COLUMN_NAME_HOME_LOCATION_ID,
  COLUMN_NAME_HOME_LOCATION_NAME
];

export function getContentItem(customer: Customer,
                               selectedColumnName: string): string {
  switch (selectedColumnName) {
    case COLUMN_NAME_ID:
      return customer.id.toString();
    case COLUMN_NAME_FIRST_NAME:
      return customer.first_name;
    case COLUMN_NAME_LAST_NAME:
      return customer.last_name;
    case COLUMN_NAME_PREFERRED_NAME:
      return customer.preferred_name;
    case COLUMN_NAME_BIRTHDAY:
      return customer.birthday;
    case COLUMN_NAME_AGE:
      const birthday = moment(customer.birthday, 'YYYY-MM-DD');
      return moment().diff(birthday, 'years').toString();
    case COLUMN_NAME_EMAIL_ADDRESS:
      return customer.email;
    case COLUMN_NAME_EMAIL_OPT_IN:
      return customer.has_opted_in_to_marketing ? 'Yes' : 'No';
    case COLUMN_NAME_ADDRESS_POSTCODE:
      return customer.address_postcode;
    case COLUMN_NAME_ADDRESS_STREET_1:
      return customer.address_street_1;
    case COLUMN_NAME_ADDRESS_STREET_2:
      return customer.address_street_2;
    case COLUMN_NAME_ADDRESS_CITY:
      return customer.address_city;
    case COLUMN_NAME_ADDRESS_STATE:
      return customer.address_state;
    case COLUMN_NAME_ADDRESS_COUNTRY:
      return customer.address_country;
    case COLUMN_NAME_TELEPHONE:
      return customer.telephone;
    case COLUMN_NAME_GOVT_ID_NUMBER:
      return customer.government_id;
    case COLUMN_NAME_GENDER:
      return customer.gender;
    case COLUMN_NAME_PRONOUNS:
      return customer.pronouns;
    case COLUMN_NAME_BARCODE:
      return customer.barcode;
    case COLUMN_NAME_CHECK_IN_COUNT:
      return customer.check_in_count.toString();
    case COLUMN_NAME_HAS_ACTIVE_MEMBERSHIP:
      return customer.has_active_membership ? 'Yes' : 'No';
    case COLUMN_NAME_HAS_ACTIVE_YOUTH_PROGRAMMING_MEMBERSHIP:
      return customer.has_active_youth_programming_membership ? 'Yes' : 'No';
    case COLUMN_NAME_HAS_ACTIVE_NON_YOUTH_PROGRAMMING_MEMBERSHIP:
      return customer.has_active_non_youth_programming_membership ? 'Yes' : 'No';
    case COLUMN_NAME_HAS_MEMBERSHIP_WITH_FUTURE_START:
      return customer.has_membership_with_future_start ? 'Yes' : 'No';
    case COLUMN_NAME_HAS_FROZEN_MEMBERSHIP:
      return customer.has_frozen_membership ? 'Yes' : 'No';
    case COLUMN_NAME_NUMBER_OF_AVAILABLE_ENTRY_PASSES:
      return customer.number_of_available_entry_passes.toString();
    case COLUMN_NAME_ACTIVE_WAIVER_EXISTS:
      return customer.active_waiver_exists ? 'Yes' : 'No';
    case COLUMN_NAME_LATEST_WAIVER_EXPIRATION_DATE:
      return customer.latest_waiver_expiration_date;
    case COLUMN_NAME_ACTIVE_PROFICIENCY_NAMES:
      return customer.active_proficiency_names.join(',');
    case COLUMN_NAME_FIRST_CHECK_IN_TIMESTAMP:
      return customer.first_check_in_timestamp;
    case COLUMN_NAME_LATEST_CHECK_IN_TIMESTAMP:
      return customer.latest_check_in_timestamp;
    case COLUMN_NAME_DAYS_SINCE_LATEST_CHECK_IN:
      if (!propExists(customer.days_since_latest_check_in)) { return null; }
      return customer.days_since_latest_check_in.toString();
    case COLUMN_NAME_FIRST_MEMBERSHIP_TIMESTAMP:
      return customer.first_membership_timestamp;
    case COLUMN_NAME_ACCOUNT_CREATED_TIMESTAMP:
      return customer.created_at;
    case COLUMN_NAME_HOME_LOCATION_ID:
      if (!propExists(customer.home_location_id)) { return null; }
      return customer.home_location_id.toString();
    case COLUMN_NAME_HOME_LOCATION_NAME:
      return customer.home_location_name;
    case COLUMN_NAME_CHECK_IN_COUNT_IN_RANGE:
      if (!propExists(customer.check_in_range_count)) { return null; }
      return customer.check_in_range_count.toString();
    default:
      const customFieldValue = getCustomerCustomFieldValue(customer, selectedColumnName);
      if (customFieldValue !== null) {
        return customFieldValue;
      }
      return null;
  }
}

export const COLUMN_HELP_TEXT: {[key: string]: string} = {
  [COLUMN_NAME_HAS_ACTIVE_YOUTH_PROGRAMMING_MEMBERSHIP]:
    'Whether the customer holds an active membership whose type is flagged as youth programming.',
  [COLUMN_NAME_HAS_ACTIVE_NON_YOUTH_PROGRAMMING_MEMBERSHIP]:
    'Whether the customer holds an active membership whose type is not flagged as youth programming.',
  [COLUMN_NAME_HAS_MEMBERSHIP_WITH_FUTURE_START]:
    'Whether the customer has a membership scheduled to start in the future.',
  [COLUMN_NAME_NUMBER_OF_AVAILABLE_ENTRY_PASSES]:
    'The count of entry passes the customer currently has available.',
  [COLUMN_NAME_LATEST_WAIVER_EXPIRATION_DATE]:
    'The expiration date of the customer\'s most recent active waiver.',
  [COLUMN_NAME_FIRST_CHECK_IN_TIMESTAMP]:
    'The timestamp of the customer\'s first check-in across all locations.',
  [COLUMN_NAME_LATEST_CHECK_IN_TIMESTAMP]:
    'The timestamp of the customer\'s most recent check-in across all locations.',
  [COLUMN_NAME_DAYS_SINCE_LATEST_CHECK_IN]:
    'The number of whole calendar days, in your organization\'s time zone, since the customer\'s most recent check-in. 0 means they checked in today. Blank for customers who have never checked in.',
  [COLUMN_NAME_FIRST_MEMBERSHIP_TIMESTAMP]:
    'The start timestamp of the customer\'s first membership.',
  [COLUMN_NAME_CHECK_IN_COUNT_IN_RANGE]:
    'The number of check-ins this customer had within the selected date range.',
};
